import type {
  ClassificationType,
  DocumenteAnexateData,
  EntityData,
} from "@/types/entity";
import type { ContactData } from "@/types/contact";
import type { SelectedService } from "@/types/service";
import type { ImpactAnalysisCalculatedResult } from "@/types/impact-analysis";
import { validateRelatieStatRoman } from "@/utils/relatieStatValidation";
import { validateSituatieServiciiUE } from "@/utils/serviciiUeValidation";

type OnboardingValidationResult = {
  isValid: boolean;
  fieldErrors: Record<string, string>;
};

export type OnboardingValidationInput = {
  entityData: Partial<EntityData>;
  selectedServices: SelectedService[];
  contactData: ContactData;
  documenteAnexate: DocumenteAnexateData | null;
  manualClassification: ClassificationType | null;
  needsArticle9: boolean;
  article9Results: ImpactAnalysisCalculatedResult;
};

const MSG_REQ = "Acest câmp este obligatoriu";
const MSG_POSITIVE = "Valoarea trebuie să fie mai mare decât 0";
const MSG_SERVICES = "Selectează cel puțin un serviciu NIS2";
const MSG_EMAIL = "Adresa de email nu este validă";
const MSG_CHECKBOX = "Această confirmare este obligatorie";
const MSG_CLASSIFICATION = "Selectează o clasificare NIS2";
const MSG_ARTICLE9 =
  "Completează evaluarea Art. 9 pentru a determina clasificarea";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isArticle9Complete(
  results: ImpactAnalysisCalculatedResult
): boolean {
  if (results.article9a === true) {
    return results.article9d !== null;
  }
  return (
    results.article9a !== null &&
    results.article9b !== null &&
    results.article9c !== null &&
    results.article9d !== null
  );
}

export function isCompanySectionComplete(
  entityData: Partial<EntityData>
): boolean {
  return Boolean(
    entityData.cui?.trim() &&
      entityData.nume_entitate?.trim() &&
      entityData.adresa?.trim() &&
      entityData.telefon?.trim() &&
      entityData.email?.trim() &&
      entityData.caen_principal?.cod?.trim()
  );
}

export function validateDocumenteAnexate(
  data: DocumenteAnexateData | null
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (data?.avem_documente == null) {
    errors["documente_anexate.avem_documente"] = MSG_REQ;
    return errors;
  }

  if (data.avem_documente === true) {
    if (!data.nr_documente?.trim()) {
      errors["documente_anexate.nr_documente"] = MSG_REQ;
    }
    if (!data.doc_dimensiune?.trim()) {
      errors["documente_anexate.doc_dimensiune"] = MSG_REQ;
    }
  }

  return errors;
}

export function validateOnboardingFields(
  input: OnboardingValidationInput
): OnboardingValidationResult {
  const {
    entityData,
    selectedServices,
    contactData,
    documenteAnexate,
    manualClassification,
    needsArticle9,
    article9Results,
  } = input;
  const errors: Record<string, string> = {};

  // Company section
  if (!entityData.nume_entitate?.trim()) {
    errors["nume_entitate"] = MSG_REQ;
  }
  if (!entityData.cui?.trim()) {
    errors["cui"] = MSG_REQ;
  }
  if (!entityData.adresa?.trim()) {
    errors["adresa"] = MSG_REQ;
  }
  if (!entityData.telefon?.trim()) {
    errors["telefon"] = MSG_REQ;
  }
  if (!entityData.email?.trim()) {
    errors["email"] = MSG_REQ;
  } else if (!EMAIL_REGEX.test(entityData.email)) {
    errors["email"] = MSG_EMAIL;
  }
  if (!entityData.caen_principal?.cod?.trim()) {
    errors["caen_principal"] = MSG_REQ;
  }

  // Financial section
  if (!entityData.nr_salariati || entityData.nr_salariati <= 0) {
    errors["nr_salariati"] = MSG_POSITIVE;
  }
  if (!entityData.cifra_afaceri || entityData.cifra_afaceri <= 0) {
    errors["cifra_afaceri"] = MSG_POSITIVE;
  }
  if (!entityData.active || entityData.active <= 0) {
    errors["active"] = MSG_POSITIVE;
  }

  // Services section
  if (selectedServices.length === 0) {
    errors["selectedServices"] = MSG_SERVICES;
  }

  // Contact persons
  const persons = [
    "reprezentantLegal",
    "responsabilSecuritate",
    "contactOperational",
  ] as const;

  for (const person of persons) {
    const data = contactData[person];
    if (!data.nume.trim()) {
      errors[`${person}.nume`] = MSG_REQ;
    }
    if (!data.prenume.trim()) {
      errors[`${person}.prenume`] = MSG_REQ;
    }
    if (!data.functie.trim()) {
      errors[`${person}.functie`] = MSG_REQ;
    }
    if (!data.email.trim()) {
      errors[`${person}.email`] = MSG_REQ;
    } else if (!EMAIL_REGEX.test(data.email)) {
      errors[`${person}.email`] = MSG_EMAIL;
    }
  }

  // Classification
  if (!manualClassification) {
    errors["manualClassification"] = MSG_CLASSIFICATION;
  }

  // Article 9
  if (needsArticle9 && !isArticle9Complete(article9Results)) {
    errors["article9"] = MSG_ARTICLE9;
  }

  // Relatie stat roman
  Object.assign(
    errors,
    validateRelatieStatRoman(entityData.relatie_stat_roman ?? null)
  );

  // Situatie servicii UE
  Object.assign(
    errors,
    validateSituatieServiciiUE(entityData.situatie_servicii_ue ?? null)
  );

  // Documente anexate
  Object.assign(errors, validateDocumenteAnexate(documenteAnexate));

  // Checkboxes
  if (!contactData.confirmaExactitate) {
    errors["confirmaExactitate"] = MSG_CHECKBOX;
  }
  if (!contactData.acceptaObligatii) {
    errors["acceptaObligatii"] = MSG_CHECKBOX;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    fieldErrors: errors,
  };
}
